import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import PositionInternalDto from '$types/serverTypes/positionInternalDto';
import { useAppDispatch } from '$hooks/use-app-dispatch.hook';
import { singlePositionSlice } from '$store/modules/position/position.reducer';
import { useAppSelector } from '$hooks/use-app-selector.hook';
import { ReactComponent as CloseModal } from '$icons/uncolored/Close.svg';
import { ReactComponent as ThumbDown } from '$icons/uncolored/thumbs-down.svg';
import { ReactComponent as Interview } from '$icons/uncolored/comments.svg';
import { ReactComponent as Arrow } from '$icons/uncolored/arrow.svg';
import Spinner from '$components/Spinner/Spinner';
import ModalInTailwind from '$components/ModalInTailwind/ModalInTailwind';
import computeLastJob from '$utils/computeLastJob';
import { CalendarModal } from '$components/Employers/CalendarModal/CalendarModal';

import classes from './SPKEmployerCandidatesList.module.css';
import ActiveValues from '../../../constants/ActiveValues';
import EmptyState from '../EmptyState/EmptyState';
import { CandidateData } from '../../../types/candidates';
import SPKEmployerPositionCandidateCard from './SPKEmployerPositionCandidateCard/SPKEmployerPositionCandidateCard';
import SPKEmployerCandidateFeedBackModal from './SPKEmployerPositionCandidateCard/SPKEmployerCandidateFeedBackModal';
import ModalCandidateActions from '../CandidateSummaryCardList/CandidateSummaryCard/CandidateSummaryCardEmployers/ModalCandidateActions/ModalCandidateActions';

interface Props {
  activeValue: ActiveValues;
  candidates: CandidateData[];
  setSwitchingId: Dispatch<SetStateAction<number | null>>;
  position: PositionInternalDto;
}
const SPKEmployerCandidatesList: FC<Props> = ({
  activeValue,
  candidates,
  setSwitchingId,
  position,
}) => {
  const [t] = useTranslation('global');
  const dispatch = useAppDispatch();
  const {
    selectedCandidate,
    isLoadingIframe,
    openFeedbackModal,
    openFullSPKProfile,
    openNotAFitModal,
    openConfirmationInterviewModal,
    openInterviewCalendarModal,
  } = useAppSelector((state) => state.position);
  const [lastJob, setLastJob] = useState<{
    title: string;
    company: null | string;
  }>({
    title: '',
    company: null,
  });

  const dictionary: {
    [key: string]: { title: string; description: string };
  } = {
    all: {
      title: t('EmployerPositions.NoCandidatesAsigned'),
      description: t('EmployerPositions.NoCandidatesSubtext'),
    },
    new: {
      title: t('EmployerPositions.NoCandidatesAsigned'),
      description: t('EmployerPositions.NoCandidatesSubtext'),
    },
    shortlisted: {
      title: t('EmployerPositions.NoShortListText'),
      description: t('EmployerPositions.NoShortListDescription'),
    },
    offerSent: {
      title: t('EmployerPositions.NoOfferSentText'),
      description: t('EmployerPositions.NoOfferSentDescription'),
    },
    requestInterview: {
      title: t('EmployerPositions.NoInterviewRequestedText'),
      description: t('EmployerPositions.NoInterviewRequestedDescription'),
    },
  };

  useEffect(() => {
    dispatch(singlePositionSlice.actions.setAllCandidates(candidates || []));
  }, [candidates, dispatch]);

  useEffect(() => {
    if (selectedCandidate?.CandidateWorkExperience?.length) {
      const lj = computeLastJob(selectedCandidate.CandidateWorkExperience);
      setLastJob(lj);
    } else {
      setLastJob({
        title: 'N/A',
        company: null,
      });
    }
  }, [selectedCandidate?.CandidateWorkExperience]);

  const keyboardArrowListener = useCallback(
    (ev: KeyboardEvent) => {
      if (ev.code === 'ArrowLeft') {
        dispatch(singlePositionSlice.actions.showPreviousCandidate());
      } else if (ev.code === 'ArrowRight') {
        dispatch(singlePositionSlice.actions.showNextCandidate());
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (openFullSPKProfile)
      document.addEventListener('keyup', keyboardArrowListener);
    else document.removeEventListener('keyup', keyboardArrowListener);

    return () => document.removeEventListener('keyup', keyboardArrowListener);
  }, [keyboardArrowListener, openFullSPKProfile]);

  if (candidates?.length === 0) {
    return (
      <EmptyState
        text={dictionary[activeValue].title}
        description={dictionary[activeValue].description}
        height="100%"
        transparent
      />
    );
  }

  return (
    <div className="flex flex-col gap-4 pb-8 mt-2">
      {/* SPK Full Profile Iframe Modal */}
      <ModalInTailwind
        isOpen={openFullSPKProfile}
        closeModal={() =>
          dispatch(singlePositionSlice.actions.setOpenFullSPKProfile(false))
        }
        blurBackground
      >
        <div className="container w-full h-screen p-4">
          <div className="h-full overflow-hidden flex flex-col">
            {/* Actions bar */}
            <div className="bg-[#d6e3ff] mb-4 rounded">
              <div className="pointer-events-auto pt-4 flex justify-between gap-4 p-4 items-center">
                <div className="flex flex-row gap-4 *:bg-spk-neutral-20 *:py-1 *:px-2 *:rounded">
                  <button
                    type="button"
                    className="text-left text-nowrap flex flex-row gap-2 items-center"
                    onClick={() => {
                      dispatch(
                        singlePositionSlice.actions.showPreviousCandidate()
                      );
                    }}
                  >
                    <Arrow className="w-4 h-4 rotate-90" />
                    {t('modalCandidateActions.prevCandidate')}
                  </button>
                  <button
                    className="text-left text-nowrap flex flex-row gap-2 items-center"
                    type="button"
                    onClick={() => {
                      dispatch(singlePositionSlice.actions.showNextCandidate());
                    }}
                  >
                    {t('modalCandidateActions.nextCandidate')}
                    <Arrow className="w-4 h-4 -rotate-90" />
                  </button>
                </div>
                <div className="flex flex-row gap-4 *:bg-spk-neutral-20 *:py-1 *:px-2 *:rounded">
                  <button
                    className="text-left text-nowrap flex flex-row gap-2 items-center !bg-spk-brightBlue text-white !px-4"
                    type="button"
                    onClick={() => {
                      dispatch(
                        singlePositionSlice.actions.setOpenFullSPKProfile(false)
                      );
                      dispatch(
                        singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                          true
                        )
                      );
                    }}
                  >
                    <Interview className="fill-white w-5 h-5" />
                    {t('modalCandidateActions.buttonInterview')}
                  </button>
                  <button
                    className="text-left text-nowrap flex flex-row gap-2 items-center text-spk-brightRed"
                    type="button"
                    onClick={() => {
                      dispatch(
                        singlePositionSlice.actions.setOpenFullSPKProfile(false)
                      );
                      dispatch(
                        singlePositionSlice.actions.setOpenNotAFitModal(true)
                      );
                    }}
                  >
                    <ThumbDown className="fill-spk-brightRed w-4 h-4" />
                    {t('modalCandidateActions.buttonReject')}
                  </button>
                  <button
                    type="button"
                    className="flex flex-row col-span-2 gap-2 items-center justify-center"
                    onClick={() => {
                      dispatch(
                        singlePositionSlice.actions.setOpenFullSPKProfile(false)
                      );
                    }}
                  >
                    {t('common.close')}
                    <CloseModal className="w-5 h-5 stroke-spk-highContrast" />
                  </button>
                </div>
              </div>
            </div>
            {/* Iframe */}
            <div className="p-4 w-full h-full bg-spk-neutral-20 rounded overflow-hidden flex flex-col">
              <div
                className={`${
                  !isLoadingIframe && 'hidden'
                } flex items-center justify-center w-full h-full`}
              >
                <Spinner width="100%" height="100%" />
              </div>
              <iframe
                title={`${selectedCandidate?.User?.FirstName} Profile`}
                src={`${process.env.REACT_APP_SPK_URL}/spk/${selectedCandidate?.Id}?hideNavbar=true`}
                className={`w-full h-full pointer-events-auto ${
                  isLoadingIframe ? 'max-h-0' : 'max-h-auto'
                } ${classes.zoomedIframe}`}
                onLoad={() =>
                  dispatch(
                    singlePositionSlice.actions.setIsLoadingIframe(false)
                  )
                }
              />
            </div>
          </div>
        </div>
      </ModalInTailwind>
      {/* Not a fit confirmation modal */}
      <ModalInTailwind
        isOpen={openNotAFitModal}
        closeModal={() =>
          dispatch(singlePositionSlice.actions.setOpenNotAFitModal(false))
        }
      >
        <ModalCandidateActions
          closeModal={() =>
            dispatch(singlePositionSlice.actions.setOpenNotAFitModal(false))
          }
          img={selectedCandidate?.User?.ProfilePicture?.Url}
          candidateFirstName={selectedCandidate?.User?.FirstName}
          candidateLastName={selectedCandidate?.User?.LastName}
          lastJob={lastJob}
          score={
            selectedCandidate && 'Score' in selectedCandidate
              ? selectedCandidate.Score
              : 0
          }
          handleOk={() => {
            dispatch(singlePositionSlice.actions.setOpenNotAFitModal(false));
            dispatch(singlePositionSlice.actions.setOpenFeedbackModal(true));
          }}
          type="reject"
        />
      </ModalInTailwind>
      {/* Not a Fit Feedback Modal */}
      <ModalInTailwind
        isOpen={openFeedbackModal}
        closeModal={() =>
          dispatch(singlePositionSlice.actions.setOpenFeedbackModal(false))
        }
      >
        <SPKEmployerCandidateFeedBackModal
          candidateId={selectedCandidate?.Id as number}
          setSwitchingId={setSwitchingId}
          closeHandler={() =>
            dispatch(singlePositionSlice.actions.setOpenFeedbackModal(false))
          }
          position={position.Id}
        />
      </ModalInTailwind>
      {/* Interview Confirmation Modal */}
      <ModalInTailwind
        isOpen={openConfirmationInterviewModal}
        closeModal={() =>
          dispatch(
            singlePositionSlice.actions.setOpenConfirmationInterviewModal(false)
          )
        }
      >
        <ModalCandidateActions
          closeModal={() =>
            dispatch(
              singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                false
              )
            )
          }
          img={selectedCandidate?.User?.ProfilePicture?.Url}
          candidateFirstName={selectedCandidate?.User?.FirstName}
          candidateLastName={selectedCandidate?.User?.LastName}
          lastJob={lastJob}
          score={
            selectedCandidate && 'Score' in selectedCandidate
              ? selectedCandidate.Score
              : 0
          }
          handleOk={() => {
            dispatch(
              singlePositionSlice.actions.setOpenConfirmationInterviewModal(
                false
              )
            );
            dispatch(
              singlePositionSlice.actions.setOpenInterviewCalendarModal(true)
            );
          }}
          type="interview"
        />
      </ModalInTailwind>
      {/* Interview calendar modal */}
      {openInterviewCalendarModal &&
        selectedCandidate &&
        'CandidatesPositionStatus' in selectedCandidate && (
          <CalendarModal
            candidateId={selectedCandidate.Id}
            setIsOpen={(value) =>
              dispatch(
                singlePositionSlice.actions.setOpenInterviewCalendarModal(value)
              )
            }
            isOpen={openInterviewCalendarModal}
            setSwitchingId={setSwitchingId}
            candidatePositionStatusId={
              selectedCandidate?.CandidatesPositionStatus?.[0]?.Id
            }
          />
        )}
      {candidates?.map((item) => (
        <SPKEmployerPositionCandidateCard
          key={`PositionCandidate-${item.Id}`}
          candidate={item}
          setSwitchingId={setSwitchingId}
          position={position}
        />
      ))}
    </div>
  );
};

export default SPKEmployerCandidatesList;
